import { promiseTimeout } from "@vueuse/core";
import type { PricingStateLoading } from "../bulk_buy/bulkBuyTypes";

// Every step states work the pricing engine actually performs (market levels from
// GlobalEacPrice, premiums for the sourcing criteria, brokerage/cancellation fees) —
// honest operational transparency, never theater the buyer can catch out.
const PRICING_LOADING_MESSAGE_KEYS = [
  "common.pricing_loading_messages.retrieving_market_levels",
  "common.pricing_loading_messages.applying_sourcing_criteria",
  "common.pricing_loading_messages.calculating_fees",
];

const PRICING_LOADING_STEP_MS = 450;
// Keep the steps on screen just long enough to register (Buell & Norton's operational
// transparency effect); a request slower than this is never padded further.
const PRICING_LOADING_MIN_MS = 1000;

export function getPricingLoadingMessages(
  translate: (key: string) => string
): string[] {
  return PRICING_LOADING_MESSAGE_KEYS.map((key) => translate(key));
}

/**
 * Shows the fixed step sequence while `request` is in flight and resolves with its result.
 * Steps advance on a fixed cadence and hold at the last one; the result is delayed only up
 * to the minimum display time. A rejected request surfaces immediately, with no padding.
 */
export async function runPricingLoadingSequence<T>(
  updateLoadingState: (state: PricingStateLoading) => void,
  messages: string[],
  request: Promise<T>
): Promise<T> {
  const startedAt = Date.now();
  let iteration = 0;
  updateLoadingState({ status: "loading", message: messages[0], iteration });

  const stepTimer = setInterval(() => {
    if (iteration >= messages.length - 1) return;
    iteration += 1;
    updateLoadingState({
      status: "loading",
      message: messages[iteration],
      iteration,
    });
  }, PRICING_LOADING_STEP_MS);

  try {
    const result = await request;
    const remaining = PRICING_LOADING_MIN_MS - (Date.now() - startedAt);
    if (remaining > 0) await promiseTimeout(remaining);
    return result;
  } finally {
    clearInterval(stepTimer);
  }
}
